import { afterNextRender, OnInit } from '@angular/core';
import { distinctUntilChanged, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { Directive, EventEmitter, Output, inject, DestroyRef, ChangeDetectorRef, Injector } from '@angular/core';

/**
 * libs
 */
import { TranslateService } from '@ngx-translate/core';

/**
 * interfaces
 */
import { IFormFieldMeta } from '../../interfaces/form-field-meta';

/**
 * @directive FormFieldControlBase
 * @description
 * **Foundation class for all form field controls** in the SADIN Angular application.
 * 
 * This abstract base class implements Angular's `ControlValueAccessor` pattern with generic type safety,
 * providing a robust foundation for custom form controls. It handles all the complex integration with
 * Angular's reactive forms, validation state management, change detection, and lifecycle management.
 * 
 * **Key Features:**
 * - 🎯 Generic type safety with `<T = unknown>`
 * - 🔄 Automatic Angular forms integration via `ControlValueAccessor`
 * - ✅ Built-in validation state tracking (valid, invalid, touched)
 * - 🎨 Automatic change detection and UI updates
 * - 🛡️ Protected hooks for safe subclass extension
 * - 🧹 Automatic cleanup with `DestroyRef`
 * 
 * **Inheritance Chain:**
 * ```
 * FormFieldControlBase<T>
 * ├── FormCheckFieldModel<boolean> (Checkboxes/Radio)
 * ├── FormFileFieldModel<File|FileList|null> (File Uploads)
 * └── FormFieldHighOrderDirectiveBase<T> (UI Components)
 *     ├── InputFieldComponent<string|number>
 *     ├── TextareaComponent<string>
 *     ├── SelectComponent<string|number>
 *     └── FileFieldComponent<File|FileList|null>
 * ```
 *
 * @template T The value type of the form field (default: unknown)
 * @implements IFormFieldMeta, ControlValueAccessor, OnInit
 *
 * @example
 * // Basic text input implementation
 * export class CustomInputComponent extends FormFieldControlBase<string> {
 *   public value: string = '';
 *   public id: string = 'custom-input';
 *   public name: string = 'custom-input';
 *   public readonly: boolean = false;
 *   public required: boolean = false;
 *   public disabled: boolean = false;
 * }
 *
 * @example
 * // Number input with custom initialization
 * export class NumberFieldComponent extends FormFieldControlBase<number> {
 *   public value: number = 0;
 *   public id: string = 'number-field';
 *   public name: string = 'number-field';
 *   public readonly: boolean = false;
 *   public required: boolean = false;
 *   public disabled: boolean = false;
 * 
 *   protected override onControlInit(): void {
 *     // Custom initialization logic
 *     console.log('Number field initialized');
 *   }
 * }
 *
 * @example
 * // Real-world usage from InputFieldComponent
 * export class InputFieldComponent extends FormFieldHighOrderDirectiveBase<string | number> {
 *   public value: string | number = '';
 *   // Inherits all base functionality from FormFieldControlBase
 *   // Adds UI-specific features like templates, labels, feedback
 * }
 */
@Directive()
export abstract class FormFieldControlBase<T = unknown> implements IFormFieldMeta, ControlValueAccessor, OnInit {

  /**
   * @property id
   * @description Unique identifier for the form field
   * 
   * Used to distinguish this field from others in the form and for accessibility purposes.
   * Must be unique within the form context.
   * 
   * @abstract
   * @required
   * @example
   * public id: string = 'email-field';
   * public id: string = 'user-name';
   */
  public abstract id: string;

  /**
   * @property name
   * @description Name attribute for form submission and accessibility
   * 
   * Used for form submission and accessibility purposes. This is the name that will
   * be sent with the form data when the form is submitted.
   * 
   * @abstract
   * @required
   * @example
   * public name: string = 'email';
   * public name: string = 'userName';
   */
  public abstract name: string;

  /**
   * @property value
   * @description Current value of the form field
   * 
   * The current value of the field. Type is generic and defined by the subclass implementation.
   * This value is synchronized with Angular's form control system.
   * 
   * @abstract
   * @required
   * @example
   * public value: string = '';           // For text inputs
   * public value: number = 0;            // For number inputs
   * public value: boolean = false;       // For checkboxes
   * public value: File | null = null;    // For file inputs
   */
  public abstract value: T;

  /**
   * @property readonly
   * @description Whether the field is read-only
   * 
   * If true, the field cannot be edited by the user but can still be focused.
   * The field will still participate in form validation and submission.
   * 
   * @abstract
   * @required
   * @example
   * public readonly: boolean = false;
   */
  public abstract readonly: boolean;

  /**
   * @property required
   * @description Whether the field is required for form validity
   * 
   * If true, the field must be filled for the form to be considered valid.
   * Angular's validation system will automatically check this property.
   * 
   * @abstract
   * @required
   * @example
   * public required: boolean = true;  // Required field
   * public required: boolean = false; // Optional field
   */
  public abstract required: boolean;

  /**
   * @property disabled
   * @description Whether the field is disabled
   * 
   * If true, the field is not interactive and excluded from form submission.
   * Disabled fields do not participate in validation or form submission.
   * 
   * @abstract
   * @required
   * @example
   * public disabled: boolean = false;
   */
  public abstract disabled: boolean;

  /**
   * @property _injector
   * @description Angular dependency injector for advanced use cases
   * 
   * Used to manually inject NgControl and other dependencies.
   * This is automatically injected and should not be overridden.
   * 
   * @internal
   * @private
   */
  private _injector: Injector = inject(Injector);

  /**
   * @property cdr
   * @description ChangeDetectorRef for triggering change detection
   * 
   * Used to manually trigger change detection in custom controls.
   * Automatically injected and available to subclasses.
   * 
   * @example
   * // Manually trigger change detection
   * this.cdr.markForCheck();
   * this.cdr.detectChanges();
   */
  public cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * @property destroyRef
   * @description DestroyRef for automatic cleanup
   * 
   * Used with RxJS operators for automatic cleanup of subscriptions.
   * Automatically injected and available to subclasses.
   * 
   * @example
   * // Automatic cleanup with takeUntilDestroyed
   * this.someObservable$
   *   .pipe(takeUntilDestroyed(this.destroyRef))
   *   .subscribe();
   */
  public destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * @property _translateService
   * @description TranslateService for translating error messages
   * 
   * Used to translate error messages using the translation service.
   * Automatically injected and available to subclasses.
   * 
   * @example
   * // Translate an error message
   * const translatedError = this._translateService.instant('error.required', { param: 'value' });
   */
  private _translateService: TranslateService = inject(TranslateService);

  /**
   * @property onChangeEvent
   * @description Emits when the value changes
   * 
   * Can be used for custom change tracking and external event handling.
   * Emits the new value of type T.
   * 
   * @output onChange
   * @example
   * // In template
   * <app-custom-field (onChange)="handleValueChange($event)"></app-custom-field>
   * 
   * // In component
   * handleValueChange(newValue: string) {
   *   console.log('Value changed to:', newValue);
   * }
   */
  @Output('onChange')
  public onChangeEvent: EventEmitter<T> = new EventEmitter<T>();

  /**
   * @property ngControl
   * @description Reference to the Angular NgControl instance
   * 
   * Set during initialization and used for form integration.
   * Provides access to the underlying form control.
   * 
   * @example
   * // Access the underlying control
   * if (this.ngControl?.control) {
   *   this.ngControl.control.setValidators([Validators.required]);
   * }
   */
  public ngControl!: NgControl;

  /**
   * @property valid
   * @description Whether the field is currently valid
   * 
   * Returns true if the control is valid according to Angular's validation system.
   * 
   * @readonly
   * @example
   * // In template
   * <span *ngIf="valid" class="valid-indicator">✓ Valid</span>
   * 
   * // In component
   * if (this.valid) {
   *   console.log('Field is valid');
   * }
   */
  public get valid(): boolean {
    return this.control?.valid ?? false;
  }

  /**
   * @property invalid
   * @description Whether the field is currently invalid
   * 
   * Returns true if the control is invalid according to Angular's validation system.
   * 
   * @readonly
   * @example
   * // In template
   * <span *ngIf="invalid" class="error-message">Field is invalid</span>
   * 
   * // In component
   * if (this.invalid) {
   *   console.log('Field has validation errors');
   * }
   */
  public get invalid(): boolean {
    return this.control?.invalid ?? false;
  }

  /**
   * @property touched
   * @description Whether the field has been touched
   * 
   * Returns true if the user has interacted with the field.
   * Used for determining when to show validation messages.
   * 
   * @readonly
   * @example
   * // In template
   * <span *ngIf="invalid && touched" class="error-message">
   *   {{ handlerErrorValue }}
   * </span>
   */
  public get touched(): boolean {
    return this.control?.touched ?? false;
  }

  /**
   * @property errors
   * @description Validation errors for the field
   * 
   * Returns the current validation errors or null if no errors.
   * 
   * @readonly
   * @example
   * // In component
   * if (this.errors) {
   *   console.log('Validation errors:', this.errors);
   * }
   */
  public get errors(): ValidationErrors | null {
    return this.ngControl?.errors;
  }

  /**
   * @property handlerErrorValue
   * @description First error value as a string
   * 
   * Returns the first error value as a string, or an empty string if no errors.
   * Useful for displaying error messages in the UI.
   * 
   * @readonly
   * @returns {string} The first error message or empty string
   * @example
   * // In template
   * <span *ngIf="invalid" class="error-message">
   *   {{ handlerErrorValue }}
   * </span>
   */
  public get handlerErrorValue(): string | null {

    // ES6 destructuring
    const  { errors } = this.control ?? {};
    
    // void
    if (errors == null) return '';

    // translate the error
    const _translatedError = this._translateError(errors) || null;

    // if the error is translated, return the translated error
    if (_translatedError !== null) return _translatedError;

    // get the first error
    const _firstError: string | null = Object.values(errors)?.at(0) ?? null;

    // return the first error
    return _firstError;
    
  }

  /**
   * @property control
   * @description Reference to the underlying Angular AbstractControl
   * 
   * Provides access to the form control for advanced operations.
   * 
   * @readonly
   * @example
   * // Access the underlying control
   * if (this.control) {
   *   this.control.markAsTouched();
   *   this.control.setValue('new value');
   * }
   */
  public get control(): AbstractControl | null {
    return this.ngControl?.control;
  }

  /**
   * @constructor
   * @description Base constructor for form field controls
   * 
   * Subclasses can override for custom initialization.
   * 
   * @example
   * constructor() {
   *   super();
   *   // Custom initialization logic
   * }
   */
  /**
   * @constructor
   * @description Base constructor for form field controls
   *
   * Registers the render-phase callback that binds this field to its form control.
   * Registering here — rather than in a lifecycle method — means a subclass cannot disable
   * the binding by overriding a hook without calling `super`.
   *
   * @example
   * constructor() {
   *   super();
   *   // Custom initialization logic
   * }
   */
  constructor() {
    afterNextRender(() => this._bindControl());
  }

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook for initialization
   *
   * Resolves the `NgControl` for this field. It is injected lazily through the injector rather
   * than the constructor because a `ControlValueAccessor` injecting its own `NgControl` eagerly
   * is a circular dependency.
   *
   * The control itself is **not** available yet — see {@link _bindControl} — so nothing that
   * needs `this.control` may happen here.
   *
   * @lifecycle OnInit
   * @example
   * // This method is automatically called by Angular
   * // Override onControlInit() instead of this method
   */
  ngOnInit(): void {
    // inject valueAccessor manually
    this.ngControl = this._injector.get(NgControl, null, {
      self: true,
      optional: true
    }) as NgControl;
  }

  /**
   * @method _bindControl
   * @description Subscribes to the control and hands off to {@link onControlInit}
   *
   * `NgControl.control` is assigned by `FormControlName.ngOnChanges` (through Angular's
   * `setUpControl`), and Angular explicitly gives no ordering guarantee between hooks of the
   * component and of the other directives on the same element. Binding from `ngOnInit` therefore
   * found `control` still `undefined` and silently skipped every subscription — leaving fields
   * that share a control (radio groups, checkbox groups) unable to observe each other, because a
   * user selection reaches the control via `setValue(..., { emitModelToViewChange: false })`,
   * which bypasses `writeValue` on every accessor.
   *
   * Render callbacks run after change detection has completed for the whole application, so the
   * control is guaranteed to be bound by this point. Fires exactly once, so no re-entry guard.
   *
   * @private
   */
  private _bindControl(): void {

    if (this.control != null) {
      // force update control ui status
      this.control
        .events
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged((prev, curr) => {
            const { source, ...prevRestRaw } = prev;
            const { source: currSource, ...currRestRaw } = curr;
            const prevRest = prevRestRaw as Record<string, any>;
            const currRest = currRestRaw as Record<string, any>;
            const prevKey = Object.keys(prevRest)[0];
            const currKey = Object.keys(currRest)[0];
            return prevRest[prevKey] === currRest[currKey];
          }),
          tap(_ => this.cdr.markForCheck()),
        )
        .subscribe();
    }

    this.onControlInit();
  }

  /**
   * @method onControlInit
   * @description Protected hook for subclasses to add custom initialization logic
   * 
   * Called at the end of `ngOnInit()`. Subclasses can override this method
   * instead of `ngOnInit()` for safer extension.
   * 
   * @protected
   * @virtual
   * @example
   * protected override onControlInit(): void {
   *   // Custom initialization logic
   *   console.log('Custom field initialized');
   *   
   *   // Set up custom validation
   *   if (this.control) {
   *     this.control.setValidators([Validators.required]);
   *   }
   * }
   */
  protected onControlInit(): void {
    // To be overridden by subclasses if needed
  }

  /**
   * @property onChange
   * @description Callback function for value changes
   * 
   * Set by Angular's ControlValueAccessor implementation.
   * Used internally by Angular forms.
   * 
   * @internal
   */
  public onChange: (_: any) => void = () => { };

  /**
   * @property onTouched
   * @description Callback function for touch events
   * 
   * Set by Angular's ControlValueAccessor implementation.
   * Used internally by Angular forms.
   * 
   * @internal
   */
  public onTouched: () => void = () => { };

  /**
   * @method writeValue
   * @description Called by form control to set initial value
   * 
   * Part of Angular's ControlValueAccessor interface.
   * Called when the form control value changes externally.
   * 
   * @param value The new value to set
   * @implements ControlValueAccessor.writeValue
   * @example
   * // This method is called by Angular forms
   * // Override if you need custom value handling
   * public override writeValue(value: string): void {
   *   this.value = value;
   *   // Additional custom logic
   * }
   */
  public writeValue(value: T): void {
    this.value = value;
  }

  /**
   * @method registerOnChange
   * @description Registers callback on value change
   * 
   * Part of Angular's ControlValueAccessor interface.
   * Called by Angular to register the change callback.
   * 
   * @param fn The callback function to register
   * @implements ControlValueAccessor.registerOnChange
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * @method registerOnTouched
   * @description Registers callback on touch
   * 
   * Part of Angular's ControlValueAccessor interface.
   * Called by Angular to register the touch callback.
   * 
   * @param fn The callback function to register
   * @implements ControlValueAccessor.registerOnTouched
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * @method setDisabledState
   * @description Enables/disables the component
   * 
   * Part of Angular's ControlValueAccessor interface.
   * Called by Angular to enable/disable the form control.
   * 
   * @param isDisabled Whether the component should be disabled
   * @implements ControlValueAccessor.setDisabledState
   * @example
   * // This method is called by Angular forms
   * // Override if you need custom disabled state handling
   * public override setDisabledState(isDisabled: boolean): void {
   *   this.disabled = isDisabled;
   *   // Additional custom logic
   * }
   */
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  /**
   * @method _translateError
   * @description Translates the error message
   * 
   * Translates the error message using the translation service.
   * @private
   * @param errors - The errors to translate
   * @returns The translated error message or null if no errors
   */
  private _translateError(errors: ValidationErrors): string | null {

    // ES6 destructuring
    const { i18n } = errors || {};

    // void validation
    if (i18n == null) return null;

    // get the error message
    const _errorMsg = this._translateService.instant(i18n.key, i18n.params);

    // return the error message
    return _errorMsg || null;

  }

} 